import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import {
  SelectableSortKeyColumnOptions,
  SelectableSortKeyDataGridColumn,
} from '../../common/react/components/dataGrid/SelectableSortKeyDataGridColumn';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { QueryString } from '../../common/react/QueryString';
import { SorterActions } from '../../common/react/SorterActions';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { DatasourceContentType } from '../../ts/models/ContentTypes';
import { IDataSourceBasicInfo, IDataSourceDetailInfo, IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { DataConnections } from '../../ts/resources/DataConnections';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { CertifiableConnectsToCell, ConnectionHoverPanel, ConnectsToCell } from './DataConnectionComponents';

export module DatasourceGrid {

  export type DatasourceGridScope =
      CommonDataGridColumns.ISelectColumnScope<IDataSourceInfo> &
      CommonDataGridColumns.IFavoriteColumnScope<IDataSourceInfo>;

  export function columns(
    translate: IReactTranslateService,
    scope: DatasourceGridScope,
    siteUrls: SiteUrls,
    actions: IActionButton<IDataSourceInfo, IDataSourceDetailInfo>[],
    getActionTarget: () => IActionTarget<IDataSourceInfo, IDataSourceDetailInfo>,
    anyActionsAllowed: boolean,
    inProjectPlace: boolean,
    inWorkbookPlace: boolean,
    angularContext: AngularContext,
    dataConnections: DataConnections,
    sorterActions: SorterActions,
    queryString: QueryString
  ): DataGrid.Column[] {

    const certificationColumn: DataGrid.Column = {
      name: 'certification-col',
      dataKey: 'certification',
      headerText: '',
      headerTooltip: translate('column_certification'),
      resizable: false,
      headerFormatter: SortableHeader.Formatter(),
      sortable: false,
      cellFormatter: DataGridCells.CertificationMarkTooltipCell(
        row => row.data.certificationDetails && row.data.certificationDetails.isCertified,
        row => row.data.certificationDetails &&
          {
            id: row.data.id,
            certifier:  {
              displayName: row.data.certificationDetails.certifier.displayName,
              url: row.data.certificationDetails.certifier.domainName
                    ? siteUrls.userUrl(row.data.certificationDetails.certifier.username, row.data.certificationDetails.certifier.domainName)
                    : ''
            },
            note: row.data.certificationDetails.certificationNote,
            date: row.data.lastRefreshedAt,
            style: { verticalAlign: 'middle' }
          }
      ),
      // TODO: Use the StyledCellFormatterWrapper when we upgrade to Data Grid 2/3 (TFS: 769624)
      cellStyle: {
        padding: '0 6px'
      }
    };

    const nameColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace({
      name: 'name-col',
      dataKey: 'name',
      headerText: translate('column_name'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DatasourceNameCellFormatter(_.identity, siteUrls, translate, inWorkbookPlace)
    });

    const nameColumnWithActions = CommonDataGridColumns.withActions(nameColumn, {
      actions: actions,
      getActionTarget: getActionTarget,
      translate: translate
    }, scope);

    const hitsColumn = CommonDataGridColumns.selectableColumnWithBars('hitsCol',
      ['hitsTotal', 'hitsLastTwelveMonthsTotal', 'hitsLastThreeMonthsTotal', 'hitsLastOneMonthTotal'],
      queryString, translate, sorterActions, 'hitsTotal');

    const connectedWorkbooksColumn: DataGrid.Column = {
      name: 'connected-workbooks-col',
      dataKey: 'connectedWorkbooksCount',
      headerText: translate('column_numWorkbooks'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.LocalizedCountWithBar(
        (data: IDataSourceInfo) => data.connectedWorkbooksCount,
        (data: IDataSourceInfo) => data.usageInfoMaxValues['connectedWorkbooksCount']),
      direction: 'desc'
    };

    const getPopoverContent = ConnectionHoverPanel.getPopoverContent(dataConnections, translate, siteUrls);

    const connectsToColumn: DataGrid.Column = {
      name: 'connects-to-col',
      dataKey: 'connectsTo',
      headerText: translate('column_connects_to'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: ConnectsToCell.cellFormatter(siteUrls, translate, getPopoverContent, ConnectionHoverPanel.shouldDisplayPanel)
    };

    const connectsToColumnWithCertification: DataGrid.Column = {
      name: 'connects-to-col',
      dataKey: 'connectsTo',
      headerText: translate('column_connects_to'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: CertifiableConnectsToCell.cellFormatter(
        row => row.data.connectionDetails &&
          row.data.connectionDetails.type === 'tableauserver' &&
          row.data.connectionDetails.certificationDetails &&
          row.data.connectionDetails.certificationDetails.isCertified,
        row => ({
          id: row.data.id,
          certifier:  {
            displayName: row.data.connectionDetails.certificationDetails.certifier.displayName,
            url: row.data.connectionDetails.certificationDetails.certifier.domainName
                ? siteUrls.userUrl(row.data.connectionDetails.certificationDetails.certifier.username, row.data.connectionDetails.certificationDetails.certifier.domainName)
                : ''
          },
          note: row.data.connectionDetails.certificationDetails.certificationNote,
          date: row.data.connectionDetails.lastRefreshedAt ? new Date(row.data.connectionDetails.lastRefreshedAt) : null,
          style: { verticalAlign: 'middle' }
        }),
        siteUrls,
        translate,
        getPopoverContent,
        ConnectionHoverPanel.shouldDisplayPanel
      )
    };

    const lastRefreshedDateColumn: DataGrid.Column = {
      name: 'last-refreshed-at-col',
      dataKey: 'lastRefreshedAt',
      headerText: translate('column_last_refresh'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: LastRefreshedCellFormatter(translate),
      direction: 'desc'
    };

    const projectNameColumn: DataGrid.Column = {
      name: 'project-name-col',
      dataKey: 'projectName',
      headerText: translate('column_project'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        return {
          text: row.data.project ? row.data.project.name : '',
          url: row.data.project ? siteUrls.projectUrl(row.data.project.id) : ''
        };
      })
    };

    const dateColumnSpec: SelectableSortKeyColumnOptions = {
      columnName: 'dateCol',
      defaultDataKey: 'lastRefreshedAt',
      items: [
        {
          dataKey: 'lastRefreshedAt',
          headerText: translate('column_last_refresh'),
          cellFormatter: LastRefreshedCellFormatter(translate)
        },
        {
          dataKey: 'updatedAt',
          headerText: translate('column_updated'),
          cellFormatter: DataGridCells.DateTimeCell('updatedAt')
        }
      ]
    };

    const switchableDateColumn: DataGrid.Column = SelectableSortKeyDataGridColumn(dateColumnSpec,
        { queryString: queryString, sorterActions: sorterActions });

    return _.compact([
      anyActionsAllowed && CommonDataGridColumns.selected(scope),
      CommonDataGridColumns.favorite(scope, row => !isEmbedded(row.data as IDataSourceBasicInfo)),
      FeatureFlags.datasourceCertificationEnabled && !inWorkbookPlace && certificationColumn,
      anyActionsAllowed ? nameColumnWithActions : nameColumn,
      !inWorkbookPlace && hitsColumn,
      !inWorkbookPlace && connectedWorkbooksColumn,
      (inWorkbookPlace && FeatureFlags.datasourceCertificationEnabled) ? connectsToColumnWithCertification : connectsToColumn,
      !inProjectPlace && !inWorkbookPlace && projectNameColumn,
      !inWorkbookPlace && CommonDataGridColumns.owner(translate, siteUrls),
      inWorkbookPlace && lastRefreshedDateColumn,
      !inWorkbookPlace && switchableDateColumn,
      CommonDataGridColumns.alert(DatasourceContentType.name, translate, angularContext)
    ]);
  }

  function isEmbedded(datasource: IDataSourceBasicInfo): boolean {
    return !!datasource.workbook;
  }

  function LastRefreshedCellFormatter(translate: IReactTranslateService): React.StatelessComponent<BodyCell.Props> {
    return function(props: BodyCell.Props) {
      const datasource = <IDataSourceInfo> props.row.data;

      const labelStyle: React.CSSProperties = {
        marginRight: 18,
        fontSize: CommonStyles.Sizes.SmallFontSize,
        textTransform: 'uppercase'
      };

      return React.createElement('div', { style: {display: 'flex'} },
        React.createElement('div', { style: labelStyle }, translate(datasource.hasExtracts ? 'extract' : 'live')),
        DataGridCells.DateTimeCell('lastRefreshedAt')(props));
    };
  }

  export function DatasourceNameCellFormatter(getDatasourceFn: (data: any) => IDataSourceBasicInfo,
                                              siteUrls: SiteUrls,
                                              translate: IReactTranslateService,
                                              inWorkbookPlace: boolean
  ): React.StatelessComponent<BodyCell.Props> {

    function publishedDatasourceCell(datasource: IDataSourceBasicInfo, props: BodyCell.Props) {
      const linkSpec: DataGridCells.ILink = {
        text: datasource.name,
        url: siteUrls.datasourceUrl(datasource.id),
        iconClassName: 'tb-icon-datasource-published',
        iconTitle: translate('datasource')
      };
      return DataGridCells.Link(() => linkSpec)(props);
    }

    function embeddedDatasourceLinkCell(datasource: IDataSourceBasicInfo, props: BodyCell.Props) {
      const linkSpec: DataGridCells.ILink = {
        text: datasource.name,
        url: siteUrls.workbookDatasourcesUrl(datasource.workbook.id),
        title: translate('datasourceEmbeddedInWorkbook_nameTooltip',
          {
            DATASOURCE_NAME: datasource.name,
            WORKBOOK_NAME: datasource.workbook.name
          }),
        iconClassName: 'tb-icon-datasource-embedded',
        iconTitle: translate('datasourceEmbeddedInWorkbook_typeTooltip')
      };
      return DataGridCells.Link(() => linkSpec)(props);
    }

    function embeddedDatasourceTextCell(datasource: IDataSourceBasicInfo, props: BodyCell.Props) {
      const textSpec: DataGridCells.ITextWithIcon = {
        text: datasource.name,
        iconClass: 'tb-icon-datasource-embedded',
        iconTitle: translate('datasourceEmbeddedInWorkbook_typeTooltip')
      };
      return DataGridCells.PlainTextCellWithIcon(() => textSpec)(props);
    }

    return function(props: BodyCell.Props): React.ReactElement<BodyCell.Props> {
      const datasource: IDataSourceBasicInfo = getDatasourceFn(props.row.data);
      if (isEmbedded(datasource)) {
        return inWorkbookPlace
          ? embeddedDatasourceTextCell(datasource, props)
          : embeddedDatasourceLinkCell(datasource, props);
      } else {
        return publishedDatasourceCell(datasource, props);
      }
    };
  }

}
